// src/utils/vehicleUtils.js

import { getDistance, isPointInPolygon as isPointInPolygonGeolib } from "geolib";
import { iconDisconnected, iconDelayed, iconDriving, iconEngineCutViolation, iconEngineCut, iconFenceViolation, iconFenced, iconParkViolation, iconPark, iconSpeedViolation, iconSpeed } from "../app/Icons"; // Make sure this path is correct for your project

// --- Time Constants (in milliseconds) ---
const LIVE_THRESHOLD = 3 * 60 * 1000; // 3 minutes
const CONNECTED_THRESHOLD = 3 * 60 * 60 * 1000; // 1 hour

/**
 * Parses a "lat,lng" string into a geolib-compatible coordinate object.
 * @param {string} coordString The coordinate string.
 * @returns {{latitude: number, longitude: number} | null}
 */
const parseCoordinate = coordString => {
    if (!coordString || !coordString.includes(",")) return null;
    const [lat, lng] = coordString.split(",");
    return { latitude: parseFloat(lat), longitude: parseFloat(lng) };
};

const getLastLogTime = vehicle => {
    if (!vehicle.lastLog) return 0;
    return new Date(vehicle.lastLog).getTime();
};

const isVehicleConnected = vehicle => Date.now() - getLastLogTime(vehicle) < CONNECTED_THRESHOLD;
const isVehicleDelayed = vehicle => Date.now() - getLastLogTime(vehicle) < CONNECTED_THRESHOLD && Date.now() - getLastLogTime(vehicle) > CONNECTED_THRESHOLD;
const isVehicleLive = vehicle => Date.now() - getLastLogTime(vehicle) < LIVE_THRESHOLD;

const isOwnerParked = (vehicle, userId) => vehicle.isOwnerParked && userId === vehicle.ownerRef;
const isDriverParked = (vehicle, userId) => vehicle.isDriverParked && userId === vehicle.driverRef;
const isOwnerFenced = (vehicle, userId) => vehicle.isOwnerFenced && userId === vehicle.ownerRef;
const isDriverFenced = (vehicle, userId) => vehicle.isDriverFenced && userId === vehicle.driverRef;
const isOwnerSpeedLimited = (vehicle, userId) => vehicle.isOwnerSpeedLimited && userId === vehicle.ownerRef;
const isDriverSpeedLimited = (vehicle, userId) => vehicle.isDriverSpeedLimited && userId === vehicle.driverRef;
const isEngineOff = vehicle => !vehicle.engine;

const getDistanceBetweenTwoPoints = (coord1, coord2) => {
    const from = parseCoordinate(coord1);
    const to = parseCoordinate(coord2);
    if (from && to) {
        return getDistance(from, to);
    }
    return 0;
};

const isPointInFence = (polygon, location) => {
    if (!location || !Array.isArray(polygon) || polygon.length < 3) return true;
    const point = parseCoordinate(location);
    if (!point) return true;

    const fence = polygon.map(p => parseCoordinate(p)).filter(Boolean);
    return isPointInPolygonGeolib(point, fence);
};

const isSpeedViolated = (vehicle, limit) => {
    if (!vehicle || !Array.isArray(vehicle.log) || vehicle.log.length === 0) return false;
    const lastLog = vehicle.log[vehicle.log.length - 1];
    return (lastLog.speedInKmh || 0) > limit;
};

/**
 * Determines the correct icon for a vehicle based on its current state.
 * @param {object} vehicle The vehicle object.
 * @param {string} userId The current user's ID.
 * @returns {L.Icon} The Leaflet icon instance.
 */
export const getVehicleIcon = (vehicle, userId) => {
    if (!isVehicleConnected(vehicle)) return iconDisconnected;
    if (!isVehicleLive(vehicle)) return iconDelayed; // Or a dedicated 'delayed' icon

    // if (isEngineOff(vehicle) && getDistanceBetweenTwoPoints(vehicle.ownerParkedLocation, vehicle.lastLocation) > 50) return iconEngineCutViolation;
    if (isOwnerParked(vehicle, userId) && getDistanceBetweenTwoPoints(vehicle.ownerParkedLocation, vehicle.lastLocation) > 50) return iconParkViolation;
    if (isDriverParked(vehicle, userId) && getDistanceBetweenTwoPoints(vehicle.driverParkedLocation, vehicle.lastLocation) > 50) return iconParkViolation;
    if (isOwnerFenced(vehicle, userId) && !isPointInFence(vehicle.ownerFence, vehicle.lastLocation)) return iconFenceViolation;
    if (isDriverFenced(vehicle, userId) && !isPointInFence(vehicle.driverFence, vehicle.lastLocation)) return iconFenceViolation;
    if (isOwnerSpeedLimited(vehicle, userId) && isSpeedViolated(vehicle, vehicle.ownerSpeedLimitValue)) return iconSpeedViolation;
    if (isDriverSpeedLimited(vehicle, userId) && isSpeedViolated(vehicle, vehicle.driverSpeedLimitValue)) return iconSpeedViolation;
    // if (isEngineOff(vehicle)) return iconEngineCut;
    if (isOwnerParked(vehicle, userId) || isDriverParked(vehicle, userId)) return iconPark;
    if (isOwnerFenced(vehicle, userId) || isDriverFenced(vehicle, userId)) return iconFenced;
    if (isOwnerSpeedLimited(vehicle, userId) || isDriverSpeedLimited(vehicle, userId)) return iconSpeed;

    return iconDriving;
};

const toRadians = degrees => (degrees * Math.PI) / 180;
const toDegrees = radians => (radians * 180) / Math.PI;

/**
 * Calculates the bearing between two coordinate strings.
 * Returns null if calculation is not possible.
 * @param {string | undefined} fromCoordinate "lat,lng" string.
 * @param {string | undefined} toCoordinate "lat,lng" string.
 * @returns {number | null} The bearing in degrees (0-360), or null.
 */
export const getBearing = (fromCoordinate, toCoordinate) => {
    // Return null if we don't have enough data to calculate
    if (!fromCoordinate || !toCoordinate) return null;

    const from = parseCoordinate(fromCoordinate);
    const to = parseCoordinate(toCoordinate);
    if (!from || !to) return null;

    // Prevent calculation on the same point
    if (from.latitude === to.latitude && from.longitude === to.longitude) return null;

    let { latitude: lat1, longitude: lon1 } = from;
    let { latitude: lat2, longitude: lon2 } = to;

    const toRadians = degrees => (degrees * Math.PI) / 180;
    const toDegrees = radians => (radians * 180) / Math.PI;

    lat1 = toRadians(lat1);
    lon1 = toRadians(lon1);
    lat2 = toRadians(lat2);
    lon2 = toRadians(lon2);

    const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
    let brng = toDegrees(Math.atan2(y, x));
    return (brng + 360) % 360;
};
